import { createElement, forwardRef, Fragment, useImperativeHandle, useRef, useState } from 'react';
import { MessageDialog, MessageDialogProps, useMessageDialogText } from '../../MessageDialog';
import { FileImporterProps, FileImporterRef } from '.';
import { ShortcutsConfig } from '..';
import { createUseTextLocalizer } from '../../../locale/createUseTextLocalizer';
import bundle from '../NovaComponents-KeyboardShortcuts-gui-icu.properties';

const useTextLocalizer = createUseTextLocalizer(bundle);

export const FileImporter = forwardRef<FileImporterRef, FileImporterProps>((props, ref) => {
  const localize = useTextLocalizer();
  const inputRef = useRef<HTMLInputElement>(null);
  const [messageDialogProps, setMessageDialogProps] = useState<MessageDialogProps>();
  const { CANCEL, CLOSE } = useMessageDialogText();

  useImperativeHandle(ref, () => ({
    open: () => {
      inputRef.current?.click();
    },
  }));

  const showError = (text: string, detailText?: string) => {
    setMessageDialogProps({
      type: 'error',
      isOpen: true,
      title: localize('importShortcuts.dialog.title'),
      text,
      detailText,
      dismissAction: {
        text: CLOSE,
        onPress: () => setMessageDialogProps((prev) => ({ ...prev!, isOpen: false })),
      },
    });
  };

  return (
    <Fragment>
      <input
        type="file"
        hidden
        // always clear the value so that selecting the same file later will trigger change event again
        value=""
        capture={false}
        accept={props.fileType}
        ref={inputRef}
        onChange={(event) => {
          const file = event.target.files?.[0];

          if (
            file &&
            (file.type === '' ||
              file.type === 'text/plain' ||
              (props.fileType && file.name.endsWith(props.fileType)))
          ) {
            readJSON(file)
              .then((data) =>
                setMessageDialogProps({
                  type: 'warning',
                  isOpen: true,
                  title: localize('importShortcuts.dialog.title'),
                  text: localize('confirmReplace.msg'),
                  firstAction: {
                    text: localize('replaceAll.button.label'),
                    onPress: () => {
                      setMessageDialogProps((prev) => ({ ...prev!, isOpen: false }));
                      props.onImport(data);
                    },
                  },
                  dismissAction: {
                    text: CANCEL,
                    isDefault: true,
                    onPress: () => setMessageDialogProps((prev) => ({ ...prev!, isOpen: false })),
                  },
                })
              )
              .catch((reason) => showError(localize('fileError.msg'), reason?.message));
          } else {
            showError(localize('invalidFile.msg'));
          }
        }}
      />
      {messageDialogProps ? <MessageDialog {...messageDialogProps} /> : null}
    </Fragment>
  );
});

const readJSON = (file: Blob) => {
  const reader = new FileReader();
  return new Promise<ShortcutsConfig>((resolve, reject) => {
    reader.onload = (e) => {
      const result = e.target?.result?.toString();
      if (result && result.length) {
        try {
          resolve(JSON.parse(result));
        } catch (ex) {
          reject(ex);
        }
      } else {
        reject();
      }
    };
    reader.onerror = reject;
    reader.readAsText(file);
  });
};
